 import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import filter from "../../assets/MentalPlus/filtres.png";
import search from "../../assets/MentalPlus/search.png";
import save from "../../assets/MentalPlus/save.png"; 
import styled, { keyframes } from "styled-components";
import Hls from "hls.js";
import { useParams } from "react-router-dom";
import {
  IonModal,
  IonTitle,
  IonSearchbar,
  IonToolbar,
  useIonRouter,
  useIonViewWillEnter,
} from "@ionic/react";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { getVideos } from "../../services/video.service";

const PAGE_SIZE = 24;
const allowedIds: string[] = []; 

const allTags: Record<string, string[]> = {
  Îles: [
    "Famille",
    "Relation Amoureuse",
    "Collège",
    "Amis",
    "Réseaux Sociaux",
    "Loisirs",
    "Mon bien-être",
  ],
  Catégorie: ["Explications", "Solutions"],
  "Réalisée par": ["Professionnel", "Communauté"],
  "Réalisée pour": [
    "Collégiens",
    "Lycéens",
    "Étudiants",
    "Jeunes actifs",
    "Sans activité",
  ],
  "Trier par": ["Popularité", "Récent", "Ancien"], 
};

const DisplayVideos: React.FC = () => {
  const [all, setAll] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hoverId, setHoverId] = useState<string | null>(null);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);


  const [query, setQuery] = useState("");
  const [savedOnly, setSavedOnly] = useState(false); 

  const observer = useRef<IntersectionObserver | null>(null);
  const firstVideoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  const { study_slug, title, contentType, type } = useParams<{
    study_slug: string;
    title: string;
    type: string;
    contentType: string;
  }>();

  const baseTags = useMemo(
    () => [title, contentType].filter(Boolean),
    [title, contentType]
  );

  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      title: "Vidéos",
      welcomePage: false,
    });
  });

 
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const tagsForApi = [...baseTags, ...selectedTags]
      .map((t) => t?.trim())
      .filter(Boolean);

    const { items } = await getVideos(
      tagsForApi.length ? { tags: tagsForApi } : undefined
    );

    const seen = new Set<string>();
    const filtered = items.filter(
      (v) =>
        (allowedIds.length === 0 || allowedIds.includes(v.uid)) &&
        !seen.has(v.uid) &&
        seen.add(v.uid)
    );

    setAll(filtered);
    setHasMore(filtered.length > PAGE_SIZE);
    setLoading(false);
  }, [baseTags, selectedTags]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);



const searchedAll = useMemo(() => {
  const q = query.trim().toLowerCase();
  if (!q) return all;

  return all.filter((v) => {
    const title = v?.meta?.title ?? "";
    return hay.includes(q);
  });
}, [all, query]);

  const finalAll = useMemo(() => {
    if (!savedOnly) return searchedAll;
    return searchedAll.filter(() => true);
  }, [searchedAll, savedOnly]);


  useEffect(() => {
    setVideos(finalAll.slice(0, PAGE_SIZE));
    setHasMore(finalAll.length > PAGE_SIZE);
  }, [finalAll]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    setLoading(true);
    setVideos((prev) => {
      const next = finalAll.slice(prev.length, prev.length + PAGE_SIZE);
      const merged = [...prev, ...next];
      setHasMore(merged.length < finalAll.length);
      setLoading(false);
      return merged;
    });
  }, [finalAll, loading, hasMore]);

 
  const lastVideoRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) loadMore();
        },
        { threshold: 1 }
      );
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMore]
  );

 
  useEffect(() => {
    const videoEl = firstVideoRef.current;
    if (hlsRef.current) {
      try {
        hlsRef.current.destroy();
      } catch {}
      hlsRef.current = null;
    }
    if (!videoEl || videos.length === 0) return;

    const src = videos[0].hlsUrl;

    const safePlay = () => {
      videoEl.muted = true;
      videoEl.playsInline = true;
      videoEl.loop = true;
      videoEl.play().catch(() => {});
    };

    if (Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;
      hls.on(Hls.Events.MANIFEST_PARSED, safePlay);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data?.fatal) {
          try {
            hls.destroy();
          } catch {}
        }
      });
      hls.loadSource(src);
      hls.attachMedia(videoEl);
    } else if (videoEl.canPlayType("application/vnd.apple.mpegurl")) {
      videoEl.src = src;
      videoEl.oncanplay = () => safePlay();
    }
    return () => {
      try {
        hlsRef.current?.destroy();
      } catch {}
      hlsRef.current = null;
    };
  }, [videos]);

  
  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };
  const clearFilters = () => {
    setSelectedTags([]);
    setPanelOpen(false);
  };

  return (
    <ResonanceLayout isConnected>
      <PageWrapper>
        {}
        <TopBar>
          <BarRow>
            <SquareAction onClick={() => setPanelOpen(true)}>
              <img className="iconImg" src={filter} alt="" />
              <span className="label">Filtres</span>
            </SquareAction>

            <SearchField>
               <IonSearchbar
                  value={query}
                  placeholder="Rechercher"
                  inputmode="search"
                  debounce={200}
                  onIonInput={(e) => setQuery(e.detail.value ?? "")}
                  showClearButton="focus"
                  showCancelButton="never"
                />
              </SearchField>
            

            <SquareAction onClick={() => setSavedOnly((v) => !v)}>
            <img className="iconImg" src={save} alt="" />
            <span className="label">Enregistrées</span>
            </SquareAction>
          </BarRow>
        </TopBar>

        {}
        <ChipsRow>
          <Chips>
            {selectedTags.map((tag) => (
              <Chip key={tag} onClick={() => toggleTag(tag)} title="Retirer ce filtre">
                {tag} ✕
              </Chip>
            ))}
            {selectedTags.length > 0 && (
              <ClearBtn onClick={clearFilters}>Réinitialiser</ClearBtn>
            )}
          </Chips>
        </ChipsRow>

        {}
        <Grid>
          {videos.map((video, i) => (
            <GridItem
              key={video.uid}
              ref={i === videos.length - 1 ? lastVideoRef : undefined}
              onMouseEnter={() => setHoverId(video.uid)}
              onMouseLeave={() => setHoverId((id) => (id === video.uid ? null : id))}
              onClick={() => {
                const tags: string[] = [];
                if (title) tags.push(title);
                if (contentType) tags.push(contentType);
                tags.push(...selectedTags);

                const tagQuery =
                  tags.length > 0
                    ? `?tags=${tags.map((t) => encodeURIComponent(t)).join(",")}`
                    : "";

                if (title && contentType) {
                  ionRouter.push(
                    `/${study_slug}/tabs/home/island/${encodeURIComponent(title)}/${encodeURIComponent(
                      contentType
                    )}/${video.uid}/default${tagQuery}`,
                    "forward",
                    "push"
                  );
                } else {
                  ionRouter.push(
                    `/${study_slug}/tabs/videos/${video.uid}/default${tagQuery}`,
                    "forward",
                    "push"
                  );
                }
              }}
            >
              <ThumbContainer>
                <>
                  <ThumbImg src={video.thumbnailUrl} alt="video thumbnail" />
                  <HoverVideo hlsUrl={video.hlsUrl} isHover={hoverId === video.uid} />
                </>
              </ThumbContainer>
            </GridItem>
          ))}

          {loading && <Loading>Chargement…</Loading>}
          {!loading && videos.length === 0 && (
            <EmptyState>Aucune vidéo ne correspond à ces filtres.</EmptyState>
          )}
        </Grid>

        {}
        {panelOpen && (
          <IonModal
            className="resonance-sheetmodal"
            isOpen={true}
            backdropDismiss={true}
            onWillDismiss={() => setPanelOpen(false)}
            initialBreakpoint={1}
            breakpoints={[0, 1]}
            canDismiss={true}
          >
            <IonToolbar>
              <IonTitle>Filtres</IonTitle>
              <CloseButton slot="end" onClick={() => setPanelOpen(false)}>
                ×
              </CloseButton>
            </IonToolbar>

            <>
              {Object.entries(allTags).map(([section, tags]) => (
                <Section key={section}>
                  <SectionTitle>{section}</SectionTitle>
                  <TagList>
                    {tags.map((tag) => (
                      <FilterTag
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        selected={selectedTags.includes(tag)}
                      >
                        {tag}
                      </FilterTag>
                    ))}
                  </TagList>
                </Section>
              ))}
            </>
          </IonModal>
        )}
      </PageWrapper>
    </ResonanceLayout>
  );
};

const HoverVideo: React.FC<{ hlsUrl: string; isHover: boolean }> = ({
  hlsUrl,
  isHover,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const readyRef = useRef(false);
  const pauseTimer = useRef<number | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    readyRef.current = false;
    if (hlsRef.current) {
      try {
        hlsRef.current.destroy();
      } catch {}
      hlsRef.current = null;
    }

    const markReadyAndMaybePlay = () => {
      readyRef.current = true;
      if (isHover) {
        el.muted = true;
        el.playsInline = true;
        el.loop = true;
        el.play().catch(() => {});
      }
    };

    if (Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;
      hls.on(Hls.Events.MANIFEST_PARSED, markReadyAndMaybePlay);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data?.fatal) {
          try {
            hls.destroy();
          } catch {}
        }
      });
      hls.loadSource(hlsUrl);
      hls.attachMedia(el);
    } else if (el.canPlayType("application/vnd.apple.mpegurl")) {
      el.src = hlsUrl;
      el.addEventListener("canplay", markReadyAndMaybePlay, { once: true });
      return () => el.removeEventListener("canplay", markReadyAndMaybePlay);
    }

    return () => {
      try {
        hlsRef.current?.destroy();
      } catch {}
      hlsRef.current = null;
    };
  }, [hlsUrl]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (pauseTimer.current) {
      window.clearTimeout(pauseTimer.current);
      pauseTimer.current = null;
    }

    if (isHover) {
      if (readyRef.current) el.play().catch(() => {});
    } else {
      pauseTimer.current = window.setTimeout(() => {
        try {
          el.pause();
        } catch {}
      }, 120);
    }
  }, [isHover]);

  return (
    <video
      ref={videoRef}
      muted
      loop
      playsInline
      preload="metadata"
      style={{
        opacity: isHover ? 1 : 0,
        transition: "opacity 120ms linear",
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
};

export default DisplayVideos;

/* -------------------------------------------------------------------------- */
/*                                   STYLED                                   */
/* -------------------------------------------------------------------------- */

const PageWrapper = styled.div`
  min-height: 100vh;
  background: white;
`;

const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  padding: 10px 8px 0 8px;
  background: white;
`;


const BarRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 10px;
`;


const SquareAction = styled.button`
  flex: 0 0 auto;
  width: 74px;
  height: 58px;
  border: none;
  border-radius: 14px;
  background: #FFCE48;
  color: #1b1b1b;
  cursor: pointer;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;

  .label {
    font-size: 12px;
    line-height: 12px;
    font-weight: 600;
    white-space: nowrap;
  }

  .iconImg {
    width: 22px;
    height: 22px;
    object-fit: contain;
    display: block;
  }

  @media (max-width: 420px) {
    width: 62px;
    height: 54px;

    .label {
      font-size: 11px;
    }

    .iconImg {
      width: 20px;
      height: 20px;
    }
  }

  @media (max-width: 360px) {
    width: 54px;

    .label {
      display: none;
    }
  }
`;


const SearchField = styled.div`
  flex: 1;
  min-width: 0;
  height: 58px;
  border-radius: 16px;
  background: white;

  display: flex;
  align-items: center;
  padding: 0 6px; /* léger padding */

  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);

  ion-searchbar {
    width: 100%;
    padding: 0;

    /* Look "input" */
    --background: transparent;
    --box-shadow: none;
    --border-radius: 16px;

    /* Texte */
    --color: #1b1b1b;
    --placeholder-color: #9a9a9a;
    --icon-color: #1b1b1b;
    --clear-button-color: #1b1b1b;

    /* Hauteur/alignement */
    --height: 58px;
  }
`;


const ChipsRow = styled.div`
  padding: 10px 0 0 0;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button`
  background: #FFCE48;
  color: #111;
  border: none;
  padding: 6px 10px;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
`;

const ClearBtn = styled.button`
  background: #eee;
  color: #333;
  border: none;
  padding: 6px 10px;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 8px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 3px;
  }
`;

const ThumbContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const GridItem = styled.div`
  background: #000;
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 9/16;
  width: 100%;
  height: auto;
  position: relative;
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Loading = styled.div`
  grid-column: span 4;
  text-align: center;
  padding: 24px 0;
  font-size: 1.2em;
  color: #555;
`;

const EmptyState = styled.div`
  grid-column: span 4;
  text-align: center;
  padding: 32px 0;
  color: #777;
`;

// --- Bottom sheet (modal) ---
const slideUp = keyframes`
  from { transform: translateY(100%); } to { transform: translateY(0); }
`;

const BottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: calc(85vh - 90px);
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 24px 24px 0 24px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: ${slideUp} 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #444;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FilterTag = styled.button<{ selected: boolean }>`
  background-color: ${(p) => (p.selected ? "#FFCE48" : "white")};
  color: ${(p) => (p.selected ? "black" : "#333")};
  font-weight: ${(p) => (p.selected ? 500 : 400)};
  border: 1px solid #ccc;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
`;

/* -------------------------------------------------------------------------- */
/*                                   ICONS                                    */
/* -------------------------------------------------------------------------- */

const FilterIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M4 6h16" />
    <path d="M7 12h10" />
    <path d="M10 18h4" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);

const BookmarkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h12a1 1 0 0 1 1 1v18l-7-4-7 4V4a1 1 0 0 1 1-1z" />
  </svg>
);
